// @flow

import _keyBy from 'lodash/keyBy';

import {
  selectRawProductsArray,
  selectCartProductOwner,
  selectCartReselleeID,
  selectProductsByType,
  addProduct,
  removeCartProduct,
  selectHasCorporateCreateProduct,
  selectHasDomainCorporateProducts,
  updateProducts,
  updateCart,
} from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';

import { selectShopCurrent } from 'src/modules/organization/selectors';
import type { Dispatch, GetState } from 'src/types';

import { selectOneCustomer } from '../../Customer/modules/customers';
import { getProductsWithNewContactPayload } from '../../DomainContacts/Wizard/modules/actions';

import { selectShouldPatchContacts } from '../../DomainContacts/Wizard/modules/selectors';

import { isProductResellable, isProductAutorenewable } from './helpers';
import { selectProductsToUpdateForOwnership } from './selectors';

// FIXME: Factorize the contents of this function so it can be reused between:
//        - Account Choice -> Cart Overview
//        - Account Choice -> Contacts Overview
//        - Contacts update -> Contacts Overview
export const updateProductsPayload =
  (props: {
    translator: TranslatorProps,
  }): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const products = selectRawProductsArray(getState());

    // Is the current organization (cart owner) a reseller?
    const current = selectShopCurrent(getState(), true);
    const reseller = current?.reseller;

    // Retrieve the products updated contacts payload
    const updatedContactsProducts = await dispatch(
      getProductsWithNewContactPayload(
        props.translator,
        selectShouldPatchContacts(getState()),
      ),
    );

    // The cart must have been flagged with its latest reselleeId in the state.
    // Note: the reselleeId can be the the reseller's id if the reseller wishes
    // to buy for himself.
    const reselleeId = selectCartReselleeID(getState()) || undefined;
    const customer = reselleeId
      ? selectOneCustomer(getState(), reselleeId)
      : undefined;
    const resell = products.some(isProductResellable) && reselleeId && reseller;

    // Retrieve the products that may need their reseller/resellee status updated.
    // Clean-up / update or add the reseller/resellee status.
    // FIXME: This should probably be done in the same place as contacts update.
    const resellableProducts = products
      // Remove old products that have been updated for their contacts
      .filter(
        product => !updatedContactsProducts.find(({ id }) => id === product.id),
      )
      // Include the new products that have been updated for their contacts
      .concat(
        updatedContactsProducts.filter(product =>
          updatedContactsProducts.find(({ id }) => id === product.id),
        ),
      )
      // Only keep resellable products
      .filter(isProductResellable)
      // Update resellable products
      .map(product => {
        const updated = {
          ...product,
          payload: {
            ...product.payload,
          },
        };

        // This product is bought from a reseller for a customer
        // Update the product's payload to explicit the relationship.
        updated.payload.resellee_id = resell ? reselleeId : undefined;
        // $FlowFixMe improve @gandi/cart ProductPayload
        updated.payload.reseller_id = resell ? current?.id : undefined;

        updated.payload.owner_name =
          resell && customer ? customer.name : undefined;

        return updated;
      });

    let cartProductOwner = selectCartProductOwner(getState()) || null;
    // Unset cart products owner if invoiced org is same than owner.
    // Cart API will fallback on the default owner, or the reseller customer.
    if (cartProductOwner === current?.id) {
      cartProductOwner = null;
    }

    // Update eligible products `autorenew` status:
    //  - Always set `autorenew: false` when buying for someone else.
    //  - Always unset the `autorenew` flag otherwise.
    //    It will be handled either by the organization's autorenew setting
    //    by the business API, or by a cart configuration in response to express intent from the user.
    const autorenew = cartProductOwner ? false : undefined;

    // $FlowFixMe[underconstrained-implicit-instantiation]
    const updatedContactsProductsById = _keyBy(updatedContactsProducts, 'id');
    // $FlowFixMe[underconstrained-implicit-instantiation]
    const resellableProductsById = _keyBy(resellableProducts, 'id');

    // Merge the updated products together:
    // - the ones with contact update
    // - the ones with reseller status update
    // - the ones eligible to `autorenew` setting.
    const updatedProducts = products
      .map(product => {
        const contactsUpdate = updatedContactsProductsById[product.id];
        const resellUpdate = resellableProductsById[product.id];
        const shouldUpdateAutorenew = isProductAutorenewable(product);

        return (
          (contactsUpdate || resellUpdate || shouldUpdateAutorenew) && {
            ...product,
            ...contactsUpdate,
            ...resellUpdate,
            payload: {
              ...(!contactsUpdate && !resellUpdate ? product.payload : {}),
              ...(contactsUpdate?.payload || {}),
              ...(resellUpdate?.payload || {}),
              autorenew,
            },
          }
        );
      })
      .filter(Boolean);

    // Update cart products owner to current value or `null` to reset.
    // Prevents desync when user switches between billed/owner organizations.
    await dispatch(updateCart(null, { product_owner_uuid: cartProductOwner }));

    // PATCH any updated products API-side.
    // WARN:
    // We do not wish the Domain products update to perform any Dry-Run here.
    // Dry-Run will happen immediately after these updates, at Cart Owner PATCH.
    return updatedProducts.length
      ? dispatch(
          updateProducts({
            productsData: updatedProducts,
            options: {
              headers: { 'X-Dry-Run': 0 },
            },
          }),
        )
      : Promise.resolve();
  };

const corporateProductType = {
  productType: 'corporate',
};
// Add or remove Corporate Pack product according to:
// - current organization is corporate?
// - cart contains corporate-reserved products?
// - cart contains corporate pack create product?
export const ensureCorporatePack =
  (
    translator: TranslatorProps,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const currentOrganization = selectShopCurrent(getState(), true);
    const isCorporate = !!currentOrganization?.corporate;
    const cartHasCorporateProducts =
      selectHasDomainCorporateProducts(getState());
    const cartHasCorporateCreateProduct =
      selectHasCorporateCreateProduct(getState());

    if (
      !isCorporate &&
      cartHasCorporateProducts &&
      !cartHasCorporateCreateProduct
    ) {
      return dispatch(
        addProduct({
          productData: {
            product_type: 'corporate',
            process: 'create',
            payload: {
              contract_type: 'monthly',
              estimated_volume: 1,
              fee_amount: 0,
              lead_type: 'online',
              duration: 1,
              label: translator.__('Corporate Pack creation'),
            },
          },
          extra: { label: translator.__('Corporate Pack creation') },
          options: {
            headers: { 'X-Dry-Run': 0 },
          },
        }),
      );
    }

    if (
      (isCorporate && cartHasCorporateCreateProduct) ||
      (cartHasCorporateCreateProduct && !cartHasCorporateProducts)
    ) {
      const corporateCreateProductId = selectProductsByType(
        getState(),
        corporateProductType,
      ).find(product => product.process === 'create')?.id;

      return corporateCreateProductId
        ? dispatch(removeCartProduct(corporateCreateProductId))
        : Promise.resolve();
    }

    return Promise.resolve();
  };

// Perform *only-necessary* ownership-related products payload updates:
// - Set current / unset previous `[reseller/resellee]_id`.
// - Set current / unset previous `autorenew`.
export const updateProductsPayloadForOwnership =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const toUpdate = selectProductsToUpdateForOwnership(getState());

    if (!toUpdate.length) {
      return Promise.resolve();
    }

    return dispatch(
      updateProducts({
        // $FlowFixMe refine @gandi/cart ProductPayload
        productsData: toUpdate,
        options: { headers: { 'X-Dry-Run': 0 } },
      }),
    );
  };

export const updateCartProductOwner =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const currentId = selectCurrentId(getState());
    let cartProductOwner = selectCartProductOwner(getState()) || null;
    // Unset cart products owner if invoiced org is same than owner.
    // Cart API will fallback on the default owner, or the reseller customer.
    if (cartProductOwner === currentId) {
      cartProductOwner = null;
    }

    // Update cart products owner to current value or `null` to reset.
    // Prevents desync when user switches between billed/owner organizations.
    await dispatch(updateCart(null, { product_owner_uuid: cartProductOwner }));
  };
